import { useState } from 'react';
import { searchHighway } from './apis/highwayApi';
import Layout from 'components/Layout';
import HighwayMap from './HighwayMap';
import './Highway.css';

const formatRoadNo = (roadNo) => {
    const number = Number(roadNo);

    if (isNaN(number)) {
        return roadNo;
    }

    const quotient = Math.floor(number / 10);
    const remainder = number % 10;

    if (remainder === 0) {
        return String(quotient);
    }

    return `${quotient}-${remainder}`;
};

function HighwaySearch() {
    const [keyword, setKeyword] = useState('');

    // 검색 결과
    const [highwayList, setHighwayList] = useState([]);

    // 선택 결과
    const [selectedList, setSelectedList] = useState(() => {
        try {
            const saved = localStorage.getItem('selectedHighwayList');

            return saved
                ? JSON.parse(saved)
                : [];

        } catch (error) {
            console.error(
                '선택 내역 불러오기 실패',
                error
            );

            return [];
        }
    });

    const [activeTab, setActiveTab] = useState('search');
    const [loading, setLoading] = useState(false);
    const [message, setMessage] = useState('');
    const [mapResetKey, setMapResetKey] = useState(0);

    const handleClearSelected = () => {
        if (selectedList.length === 0) {
            return;
        }

        if (!window.confirm('선택된 내역을 모두 삭제하시겠습니까?')) {
            return;
        }

        setSelectedList([]);

        localStorage.removeItem('selectedHighwayList');
    };

    // ---------------------------------
    // 검색
    // ---------------------------------
    const handleSearch = async () => {
        setActiveTab('search');

        const value = keyword.trim();

        if (!value) {
            setHighwayList([]);
            setMessage('노선명(또는 도로번호)과 거리를 입력해주세요.');
            return;
        }

        const search = value.split(/\s+/);

        if (search.length !== 2) {
            setHighwayList([]);
            setMessage('예: 경부 15.3 / 55 15.3 / 55-1 15.3 형식으로 입력해주세요.');
            return;
        }

        const searchTarget = search[0];
        const distance = search[1];

        if (isNaN(Number(distance))) {
            setHighwayList([]);
            setMessage('거리는 숫자로 입력해주세요.');
            return;
        }

        // 도로번호 형식
        const roadNumberPattern = /^\d+(-\d+)?$/;

        // 도로명 또는 도로번호
        if (!roadNumberPattern.test(searchTarget)) {
            // 도로명은 문자이므로 그대로 통과
        }

        try {
            setLoading(true);
            setMessage('');

            const data = await searchHighway(value);

            if (Array.isArray(data)) {
                setHighwayList(data);

                if (data.length === 0) {
                    setMessage('조회된 데이터가 없습니다.');
                }
            } else {
                console.error(
                    '조회 결과가 배열이 아닙니다.',
                    data
                );

                setHighwayList([]);
                setMessage('조회 결과 형식이 올바르지 않습니다.');
            }

        } catch (error) {
            console.error(error);

            setHighwayList([]);
            setMessage('조회 중 오류가 발생했습니다.');

        } finally {
            setLoading(false);
        }
    };

    // ---------------------------------
    // Enter 검색
    // ---------------------------------
    const handleKeyDown = (e) => {
        if (e.key === 'Enter') {
            handleSearch();
        }
    };

    // ---------------------------------
    // 선택 여부
    // ---------------------------------
    const isSelected = (item) => {
        return selectedList.some(
            selected =>
                selected.seq === item.seq
        );
    };

    // ---------------------------------
    // 선택
    // ---------------------------------
    const handleSelect = (item) => {
        if (isSelected(item)) {
            return;
        }

        const newList = [
            ...selectedList,
            item
        ];

        setSelectedList(newList);

        localStorage.setItem(
            'selectedHighwayList',
            JSON.stringify(newList)
        );
    };

    // ---------------------------------
    // 선택 취소
    // ---------------------------------
    const handleRemove = (item) => {
        const newList =
            selectedList.filter(
                selected =>
                    selected.seq !== item.seq
            );

        setSelectedList(newList);

        localStorage.setItem(
            'selectedHighwayList',
            JSON.stringify(newList)
        );
    };

    // ---------------------------------
    // 현재 지도에 표시할 목록
    // ---------------------------------
    const mapList =
        activeTab === 'search'
            ? highwayList
            : selectedList;

    return (
        <Layout title="고속도로 정보 조회">
            <div style={styles.container}>
                {/* 검색 */}
                <div style={styles.searchBox}>
                    <div>
                        <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                            <input
                                type="text"
                                value={keyword}
                                onChange={(e) => setKeyword(e.target.value)}
                                onKeyDown={handleKeyDown}
                                placeholder="예: 경부 15.3 / 55 15.3 / 55-1 15.3"
                                style={styles.input}
                            />

                            <button
                                onClick={handleSearch}
                                disabled={loading}
                                style={styles.button}
                            >
                                {loading ? '조회 중...' : '조회'}
                            </button>
                        </div>

                        <div style={styles.helpText}>
                            (거리는 정수, 소수점 모두 가능)
                        </div>
                    </div>
                </div>

                {/* 메시지 */}
                {message && (
                    <div style={styles.message}>
                        {message}
                    </div>
                )}

                {/* 탭 */}
                <div style={styles.tabs}>
                    <button
                        onClick={() => {
                            setActiveTab('search');
                            setMapResetKey(prev => prev + 1);
                        }}
                        style={
                            activeTab === 'search'
                                ? styles.activeTab
                                : styles.tab
                        }
                    >
                        검색 내역 ({highwayList.length})
                    </button>

                    <button
                        onClick={() => {
                            setActiveTab('selected');
                            setMapResetKey(prev => prev + 1);
                        }}
                        style={
                            activeTab === 'selected'
                                ? styles.activeTab
                                : styles.tab
                        }
                    >
                        선택 내역 ({selectedList.length})
                    </button>
                </div>

                {/* 목록 */}
                {activeTab === 'search' && (
                    <>
                        {highwayList.length > 0 && (
                            <table style={styles.table}>
                                <thead>
                                    <tr>
                                        <th className="hide-mobile">SEQ</th>
                                        <th className="hide-mobile">ID</th>
                                        <th>노선명</th>
                                        <th>도로번호</th>
                                        <th className="hide-mobile">위도</th>
                                        <th className="hide-mobile">경도</th>
                                        <th>거리</th>
                                        <th>선택</th>
                                    </tr>
                                </thead>

                                <tbody>
                                    {highwayList.map(
                                        (item) => (
                                            <tr key={item.seq}>
                                                <td className="hide-mobile">
                                                    {item.seq}
                                                </td>

                                                <td className="hide-mobile">
                                                    {item.id}
                                                </td>

                                                <td>
                                                    {item.name}
                                                </td>

                                                <td>
                                                    {formatRoadNo(item.road_no)}
                                                </td>

                                                <td className="hide-mobile">
                                                    {item.lat}
                                                </td>

                                                <td className="hide-mobile">
                                                    {item.lng}
                                                </td>

                                                <td>
                                                    {item.distance}
                                                </td>

                                                <td>
                                                    <button
                                                        onClick={() =>
                                                            handleSelect(item)
                                                        }
                                                        disabled={
                                                            isSelected(item)
                                                        }
                                                        style={
                                                            isSelected(item)
                                                                ? styles.selectedButton
                                                                : styles.selectButton
                                                        }
                                                    >
                                                        {
                                                            isSelected(item)
                                                                ? '선택됨'
                                                                : '선택'
                                                        }
                                                    </button>
                                                </td>
                                            </tr>
                                        )
                                    )}
                                </tbody>
                            </table>
                        )}
                    </>
                )}

                {/* 선택 내역 */}
                {activeTab === 'selected' && (
                    <>
                        {selectedList.length > 0 ? (
                            <>
                                <div style={styles.selectedHeader}>
                                    <h3>
                                        선택된 내역 ({selectedList.length})
                                    </h3>

                                    <button
                                        type="button"
                                        onClick={handleClearSelected}
                                        disabled={selectedList.length === 0}
                                        style={{
                                            ...styles.deleteAllButton,
                                            opacity: selectedList.length === 0 ? 0.5 : 1,
                                            cursor: selectedList.length === 0
                                                ? 'default'
                                                : 'pointer'
                                        }}
                                    >
                                        전체 삭제
                                    </button>
                                </div>

                                <table style={styles.table}>
                                    <thead>
                                        <tr>
                                            <th className="hide-mobile">SEQ</th>
                                            <th>노선명</th>
                                            <th>도로번호</th>
                                            <th className="hide-mobile">위도</th>
                                            <th className="hide-mobile">경도</th>
                                            <th>거리</th>
                                            <th>삭제</th>
                                        </tr>
                                    </thead>

                                    <tbody>
                                        {selectedList.map(
                                            (item) => (
                                                <tr key={item.seq}>
                                                    <td className="hide-mobile">
                                                        {item.seq}
                                                    </td>

                                                    <td>
                                                        {item.name}
                                                    </td>

                                                    <td>
                                                        {formatRoadNo(item.road_no)}
                                                    </td>

                                                    <td className="hide-mobile">
                                                        {item.lat}
                                                    </td>

                                                    <td className="hide-mobile">
                                                        {item.lng}
                                                    </td>

                                                    <td>
                                                        {item.distance}
                                                    </td>

                                                    <td>
                                                        <button
                                                            onClick={() =>
                                                                handleRemove(item)
                                                            }
                                                            style={
                                                                styles.deleteButton
                                                            }
                                                        >
                                                            삭제
                                                        </button>
                                                    </td>
                                                </tr>
                                            )
                                        )}
                                    </tbody>
                                </table>
                            </>
                        ) : (
                            <div style={styles.empty}>
                                선택된 도로가 없습니다.
                            </div>
                        )}
                    </>
                )}

                {/* 지도 */}
                <div style={styles.mapContainer}>
                    <h3>
                        {activeTab === 'search'
                            ? '검색 내역 지도'
                            : '선택 내역 지도'}
                    </h3>

                    <HighwayMap
                        key={mapResetKey}
                        highwayList={mapList}
                    />
                </div>
            </div>
        </Layout>
    );
}

const styles = {
    container: {
        width: '100%',
        maxWidth: '1600px',
        boxSizing: 'border-box',
        margin: 0,
        padding: '30px 0',
        fontFamily: 'Arial, sans-serif'
    },
    searchBox: {
        display: 'flex',
        gap: '10px',
        marginBottom: '20px'
    },
    input: {
        width: '300px',
        height: '40px',
        padding: '0 12px',
        fontSize: '16px',
        border: '1px solid #ccc',
        borderRadius: '4px'
    },
    button: {
        width: '80px',
        height: '42px',
        border: 'none',
        borderRadius: '4px',
        backgroundColor: '#1976d2',
        color: '#fff',
        fontSize: '15px',
        cursor: 'pointer'
    },
    message: {
        padding: '15px',
        marginBottom: '15px',
        color: '#d32f2f',
        backgroundColor: '#fff3f3',
        borderRadius: '4px'
    },
    tabs: {
        display: 'flex',
        borderBottom: '1px solid #ddd',
        marginBottom: '20px'
    },
    tab: {
        padding: '12px 25px',
        border: 'none',
        backgroundColor: '#f5f5f5',
        cursor: 'pointer',
        fontSize: '15px'
    },
    activeTab: {
        padding: '12px 25px',
        borderTop: '1px solid #ddd',
        borderLeft: '1px solid #ddd',
        borderRight: '1px solid #ddd',
        borderBottom: '3px solid #1976d2',
        backgroundColor: '#fff',
        color: '#1976d2',
        fontWeight: 'bold',
        cursor: 'pointer',
        fontSize: '15px'
    },
    table: {
        width: '100%',
        borderCollapse: 'collapse',
        textAlign: 'center'
    },
    selectButton: {
        padding: '5px 12px',
        border: 'none',
        borderRadius: '4px',
        backgroundColor: '#1976d2',
        color: '#fff',
        cursor: 'pointer'
    },
    selectedButton: {
        padding: '5px 12px',
        border: 'none',
        borderRadius: '4px',
        backgroundColor: '#999',
        color: '#fff',
        cursor: 'default'
    },
    deleteButton: {
        padding: '5px 12px',
        border: 'none',
        borderRadius: '4px',
        backgroundColor: '#d32f2f',
        color: '#fff',
        cursor: 'pointer'
    },
    empty: {
        padding: '30px',
        textAlign: 'center',
        color: '#888'
    },
    mapContainer: {
        marginTop: '30px'
    },
    selectedHeader: {
        display: 'flex',
        justifyContent: 'space-between',
        alignItems: 'center',
        marginBottom: '15px'
    },
    deleteAllButton: {
        padding: '8px 15px',
        border: '1px solid #d32f2f',
        borderRadius: '4px',
        backgroundColor: '#fff',
        color: '#d32f2f',
        fontSize: '14px',
        cursor: 'pointer'
    },
    emptyMessage: {
        padding: '30px',
        textAlign: 'center',
        color: '#888',
        backgroundColor: '#f8f8f8',
        borderRadius: '4px'
    },
    helpText: {
        marginTop: '0px',
        marginLeft: '12px',
        fontSize: '14px',
        color: '#888',
    }
};

export default HighwaySearch;
